import { useMemo, useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Button, Fade, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import Modal from 'src/presentations/components/molecules/modal';
import Snackbar from 'src/presentations/components/atom/snackbar/Snackbar';
import IconUpload from 'src/assets/images/icons/document-uploaded.svg';
import { useRecalculationToastSelector } from './RecalculationToastSelector';
import { IToastResultDTO } from './types';
import { RecalculationCard } from './RecalculationCard';

const StyledModal = styled(Modal)({
  zIndex: 9999,

  '& .MuiDialog-paper': {
    maxWidth: '1000px',
    height: 'calc(100% - 64px)',
    borderRadius: '16px',

    '& .MuiDialogContent-root': {
      padding: '0',
      backgroundColor: '#F5F6FA',
    },
  },
});

export const RecalculationToast = () => {
  const [isShowDetails, setIsShowDetails] = useState(false);
  const { isShowNotification, currentShipmentInvoiceId, affectedShipmentsData, closeToast } = useRecalculationToastSelector();
  const [showWithDelay, setShowWithDelay] = useState(false);

  // Handle delayed animation entrance
  useEffect(() => {
    if (isShowNotification) {
      // Add a small delay before showing the animation for better visibility
      const delayTimer = setTimeout(() => {
        setShowWithDelay(true);
      }, 100);

      return () => clearTimeout(delayTimer);
    }

    setShowWithDelay(false);
    return undefined;
  }, [isShowNotification]);
  useEffect(() => {
    let timer: NodeJS.Timeout;

    const startAutoHideTimer = (delay: number) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        closeToast();
      }, delay);
    };

    if (isShowNotification && !isShowDetails) {
      // Start 10 second timer when notification shows and details not open
      startAutoHideTimer(10000);
    } else if (isShowNotification && isShowDetails) {
      // Clear timer when details are open
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isShowNotification, isShowDetails, closeToast]);

  const affectedItems = useMemo(() => {
    const items: IToastResultDTO[] = [];
    // Data now always comes from SSE via RecalculationToastSelector context
    const dataSource = affectedShipmentsData;

    if (dataSource) {
      if (dataSource?.affectedInvoices?.length > 0) {
        dataSource?.affectedInvoices.forEach((item) => {
          items.push({
            invoiceId: item?.invoiceId,
            invoiceNumber: item?.invoiceNumber,
            invoiceStatus: item?.invoiceStatus,
            workflowStatus: item?.invoiceStatus === 'APPROVED' ? 'RECALCULATION_READY_FOR_PAYMENT' : 'RECALCULATION_REJECT',
          });
        });
      }
      if (dataSource?.flaggedShipmentInvoices?.length > 0) {
        dataSource?.flaggedShipmentInvoices.forEach((item) => {
          items.push({
            invoiceId: item?.invoiceId,
            invoiceNumber: item?.invoiceNumber,
            shipmentNumber: item?.shipmentNumber,
            invoiceStatus: item?.status,
            workflowStatus: `RECALCULATION_${item?.currentDeviationWorkflow}`,
            deviations: item?.deviationTypeCountMap,
          });
        });
      }
      if (dataSource?.resolvedShipmentInvoices?.length > 0) {
        dataSource?.resolvedShipmentInvoices.forEach((item) => {
          items.push({
            invoiceId: item?.invoiceId,
            invoiceNumber: item?.invoiceNumber,
            shipmentNumber: item?.shipmentNumber,
            invoiceStatus: item?.status,
            workflowStatus: `RECALCULATION_RESOLVED`,
            deviations: item?.deviationTypeCountMap,
          });
        });
      }
    }
    return items;
  }, [affectedShipmentsData]);

  if (!isShowNotification || affectedItems?.length === 0) {
    return null;
  }

  return (
    <>
      <Slide direction='up' in={showWithDelay} timeout={600}>
        <div>
          <Fade in={showWithDelay} timeout={500}>
            <div>
              {isShowNotification && (
                <Snackbar
                  zIndex={9999}
                  autoSidebarPlacement
                  icon={<img src={IconUpload} alt='' />}
                  content={<div className='flex items-center'>{affectedItems?.length ?? 0} Shipments impacted after recalculations</div>}
                  showDivider
                  actions={
                    <>
                      <button type='button' className='flex items-center gap-10 text-green-70 font-semibold' onClick={() => setIsShowDetails(true)}>
                        Show Details
                      </button>
                      <button type='button' aria-label='Close' onClick={() => closeToast()}>
                        <CloseIcon sx={{ color: '#808080' }} />
                      </button>
                    </>
                  }
                />
              )}
            </div>
          </Fade>
        </div>
      </Slide>
      <StyledModal fullWidth maxWidth='md' open={isShowDetails} onClose={() => setIsShowDetails(false)}>
        <div className='relative bg-neutral-10 flex flex-col h-full'>
          <div className='px-24 pb-20 pt-24 border-b border-neutral-20 bg-white'>
            <h3 className='text-24 font-semibold text-blue_grey-90 mt-12'>{affectedItems?.length ?? 0} Shipments Impacted after Recalculations</h3>
          </div>

          <div className='flex-1 p-24 overflow-auto flex flex-col gap-10'>
            {affectedItems.map((item) => (
              <div key={`affected-item-${item?.invoiceId}`} className=''>
                <RecalculationCard {...item} />
              </div>
            ))}
          </div>

          <div className='mt-auto px-24 py-20 border-t border-neutral-20 flex items-center justify-end gap-12 bg-white'>
            <Button
              type='button'
              variant='outlined'
              color='inherit'
              onClick={() => setIsShowDetails(false)}
              sx={{ textTransform: 'capitalize', borderColor: '#dfdfdf' }}
            >
              Close
            </Button>
          </div>
        </div>
      </StyledModal>
    </>
  );
};
